// Investor + role selectors shown in UserFormModal when creating a Hub member.
// Lists only existing investors — creating a new one is a separate flow.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@startus/lib';
import { useInvestors } from '../hooks/useInvestors';
import { INVESTOR_MEMBER_ROLES, type InvestorMemberRole } from '../types/investor';

interface HubMemberFieldsProps {
  investorId: string;
  role: InvestorMemberRole | '';
  onInvestorChange: (investorId: string) => void;
  onRoleChange: (role: InvestorMemberRole) => void;
  /** Already-translated messages. */
  errors: { investorId?: string; role?: string };
}

const HubMemberFields: React.FC<HubMemberFieldsProps> = ({
  investorId,
  role,
  onInvestorChange,
  onRoleChange,
  errors,
}) => {
  const { t } = useTranslation();
  // Mounted only in Hub mode, so the list is fetched only when needed.
  const { investors, loading, error } = useInvestors();

  return (
    <>
      <div className="form-control mb-4">
        <div className="grid grid-cols-[auto_1fr] items-center gap-3">
          <label htmlFor="hub-investor" className="label-text">
            {t('admin:users.hub.investor')}
          </label>
          <select
            id="hub-investor"
            className="select select-bordered w-full"
            value={investorId}
            onChange={(e) => onInvestorChange(e.target.value)}
            disabled={loading || !!error}
            required
          >
            <option value="" disabled>
              {loading
                ? t('admin:users.hub.loadingInvestors')
                : t('admin:users.hub.selectInvestor')}
            </option>
            {investors.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.name}
              </option>
            ))}
          </select>
          {error ? (
            <span className="text-error text-xs mt-1 ml-auto" role="alert">
              {getErrorMessage(error, t)}
            </span>
          ) : (
            errors.investorId && (
              <span className="text-error text-xs mt-1 ml-auto">{errors.investorId}</span>
            )
          )}
          {!loading && !error && investors.length === 0 && (
            <span className="text-xs mt-1 ml-auto">{t('admin:users.hub.noInvestors')}</span>
          )}
        </div>
      </div>
      <div className="form-control mb-4">
        <div className="grid grid-cols-[auto_1fr] items-center gap-3">
          <label htmlFor="hub-role" className="label-text">
            {t('admin:users.hub.role')}
          </label>
          <select
            id="hub-role"
            className="select select-bordered w-full"
            value={role}
            onChange={(e) => onRoleChange(e.target.value as InvestorMemberRole)}
            required
          >
            <option value="" disabled>
              {t('admin:users.hub.selectRole')}
            </option>
            {INVESTOR_MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`admin:users.hub.roles.${r}`)}
              </option>
            ))}
          </select>
          {errors.role && <span className="text-error text-xs mt-1 ml-auto">{errors.role}</span>}
        </div>
      </div>
    </>
  );
};

export default HubMemberFields;
